/*
 * <carousel-slider> — fade carousel for the "Carousel" section.
 *
 * Features:
 *  - Prev / Next arrows that loop (first <- wraps to last, last -> wraps to first)
 *  - 7s auto-rotate (configurable), pauses on hover, focus, swipe and when the tab is hidden
 *  - Crossfade between slides (opacity transition handled in CSS via the .is-active class)
 *  - Pagination dots that reflect the active slide
 *  - Touch swipe support
 *
 * Slides are stacked on top of each other in CSS; this script just moves the
 * `.is-active` class between slides (and dots), and CSS cross-fades them.
 */
if (!customElements.get('carousel-slider')) {
  class CarouselSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.carousel__viewport');
      this.slides = Array.from(this.querySelectorAll('.carousel__slide'));
      this.count = this.slides.length;
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion;
      this.delay = parseInt(this.dataset.speed, 10) || 7000;
      this.timer = null;

      if (this.count === 0) return;

      // Nothing to rotate with a single slide — hide controls and bail.
      if (this.count <= 1) {
        [this.prevBtn, this.nextBtn].forEach((b) => b && (b.hidden = true));
        this.dots.forEach((d) => (d.hidden = true));
        return;
      }

      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this._activate(this.index);

      this.prevBtn && this.prevBtn.addEventListener('click', () => this._onArrow(-1));
      this.nextBtn && this.nextBtn.addEventListener('click', () => this._onArrow(1));
      this.dots.forEach((dot, i) =>
        dot.addEventListener('click', () => {
          this.goTo(i);
          this._restart();
        })
      );

      this.addEventListener('mouseenter', () => this._stop());
      this.addEventListener('mouseleave', () => this._start());
      this.addEventListener('focusin', () => this._stop());
      this.addEventListener('focusout', () => this._start());
      document.addEventListener('visibilitychange', () =>
        document.hidden ? this._stop() : this._start()
      );

      this._initSwipe();
      this._initDesignMode();

      // The visual editor drives the carousel to a slide when that slide's block
      // is selected, and holds autoplay so it stays put while being edited.
      this.addEventListener('carousel:editor-select', (e) => {
        this.editorHeld = true;
        this._stop();
        const i = e.detail && e.detail.index;
        if (typeof i === 'number' && i >= 0) this.goTo(i);
      });
      this.addEventListener('carousel:editor-release', () => {
        this.editorHeld = false;
        this._start();
      });

      this._start();
    }

    disconnectedCallback() {
      this._stop();
    }

    _activate(i) {
      // Tag the outgoing slide so its motion animation keeps running while it
      // crossfades out (dropping the animation mid-fade snaps the image back).
      // The tag is cleared after the longest possible fade (2.5s).
      this.slides.forEach((slide, n) => {
        if (slide.classList.contains('is-active') && n !== i) {
          slide.classList.add('is-leaving');
          clearTimeout(slide._leaveTimer);
          slide._leaveTimer = setTimeout(() => slide.classList.remove('is-leaving'), 2600);
        }
      });
      this.slides.forEach((slide, n) => slide.classList.toggle('is-active', n === i));
      this.dots.forEach((dot, n) => {
        const active = n === i;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    goTo(i) {
      const next = ((i % this.count) + this.count) % this.count; // wrap both directions
      if (next === this.index) return;
      this.index = next;
      this._activate(this.index);
    }

    next() {
      this.goTo(this.index + 1);
    }

    prev() {
      this.goTo(this.index - 1);
    }

    _onArrow(dir) {
      dir > 0 ? this.next() : this.prev();
      this._restart();
    }

    _start() {
      if (!this.autoplay || this.timer || this.editorHeld) return;
      this.timer = setInterval(() => this.next(), this.delay);
    }

    _stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    _restart() {
      this._stop();
      this._start();
    }

    _initSwipe() {
      let startX = null;
      this.viewport.addEventListener(
        'touchstart',
        (e) => {
          startX = e.touches[0].clientX;
        },
        { passive: true }
      );
      this.viewport.addEventListener(
        'touchend',
        (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 40) {
            dx < 0 ? this.next() : this.prev();
            this._restart();
          }
          startX = null;
        },
        { passive: true }
      );
    }

    _initDesignMode() {
      if (!window.Shopify || !Shopify.designMode) return;
      this.addEventListener('shopify:block:select', (e) => {
        this._stop();
        const i = this.slides.indexOf(e.target.closest('.carousel__slide'));
        if (i >= 0) this.goTo(i);
      });
      this.addEventListener('shopify:block:deselect', () => this._start());
    }
  }

  customElements.define('carousel-slider', CarouselSlider);
}
